import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/**
 * Draw-call reduction for the code-built glTF models: every mesh arrives as
 * one primitive per material (matte / gloss / metal / glow), i.e. 3–6 draw
 * calls per body and again in the shadow pass. The materials only differ in
 * roughness, metalness and emissive, so at load time the primitives of a mesh
 * are merged into one geometry that carries those values per vertex
 * (`afRM` = roughness, metalness; `afEm` = emissive × strength) and one
 * `MeshStandardMaterial` that reads them. A hero, monster, weapon or prop
 * then costs a single draw call.
 */

/** Emissive tint shared by merged materials that are not per-actor (props). */
const NO_TINT = { value: new THREE.Color(1, 1, 1) };
const NO_MODE = { value: 0 };

/** Per-instance emissive override: `uEmMode` 1 replaces the glow hue with `uEmTint` (keeps its strength). */
export interface EmissiveTintUniforms {
  uEmTint: { value: THREE.Color };
  uEmMode: { value: number };
}

/**
 * Shader edits for merged materials (call first in any `onBeforeCompile` of
 * a material with `userData.merged`).
 */
export function applyMergedChunks(shader: THREE.WebGLProgramParametersWithUniforms, tint: EmissiveTintUniforms = { uEmTint: NO_TINT, uEmMode: NO_MODE }): void {
  shader.uniforms.uEmTint = tint.uEmTint;
  shader.uniforms.uEmMode = tint.uEmMode;
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nattribute vec2 afRM;\nattribute vec3 afEm;\nvarying vec2 vAfRM;\nvarying vec3 vAfEm;')
    .replace('#include <begin_vertex>', '#include <begin_vertex>\nvAfRM = afRM;\nvAfEm = afEm;');
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', '#include <common>\nvarying vec2 vAfRM;\nvarying vec3 vAfEm;\nuniform vec3 uEmTint;\nuniform float uEmMode;')
    .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\nroughnessFactor *= vAfRM.x;')
    .replace('#include <metalnessmap_fragment>', '#include <metalnessmap_fragment>\nmetalnessFactor *= vAfRM.y;')
    .replace(
      '#include <emissivemap_fragment>',
      `#include <emissivemap_fragment>
      {
        vec3 afE = vAfEm;
        if (uEmMode > 0.5) afE = uEmTint * max(afE.r, max(afE.g, afE.b));
        totalEmissiveRadiance += afE;
      }`,
    );
}

function mergedMaterial(): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ name: 'M_merged', vertexColors: true, roughness: 1, metalness: 1 });
  m.envMapIntensity = 0.7;
  m.userData.merged = true;
  m.onBeforeCompile = (s) => applyMergedChunks(s);
  m.customProgramCacheKey = () => 'af-merged';
  return m;
}

function layoutKey(g: THREE.BufferGeometry): string {
  return Object.entries(g.attributes)
    .map(([k, a]) => `${k}:${a.array.constructor.name}:${a.itemSize}:${a.normalized ? 1 : 0}`)
    .sort()
    .join('|');
}

/** Merges the primitive meshes of one glTF mesh (siblings under `group`); returns the merged mesh or null. */
function mergeGroup(group: THREE.Object3D): THREE.Mesh | null {
  const prims = group.children.filter((c) => (c as THREE.Mesh).isMesh && c.children.length === 0) as THREE.Mesh[];
  if (prims.length < 2) return null;
  const first = prims[0] as THREE.Mesh;
  const skinned = (first as THREE.SkinnedMesh).isSkinnedMesh === true;
  const layout = layoutKey(first.geometry);
  for (const p of prims) {
    const m = p.material;
    if (Array.isArray(m) || !(m instanceof THREE.MeshStandardMaterial)) return null;
    if (m.transparent || m.side !== THREE.FrontSide || m.map || m.normalMap || m.emissiveMap) return null;
    if (!p.position.equals(first.position) || !p.quaternion.equals(first.quaternion) || !p.scale.equals(first.scale)) return null;
    if (((p as THREE.SkinnedMesh).isSkinnedMesh === true) !== skinned) return null;
    if (skinned && (p as THREE.SkinnedMesh).skeleton !== (first as THREE.SkinnedMesh).skeleton) return null;
    if (layoutKey(p.geometry) !== layout || !p.geometry.index) return null;
    if (Object.keys(p.geometry.morphAttributes).length > 0) return null;
  }
  const geos = prims.map((p) => {
    const g = p.geometry.clone();
    const mat = p.material as THREE.MeshStandardMaterial;
    const n = g.getAttribute('position').count;
    const rm = new Float32Array(n * 2);
    const em = new Float32Array(n * 3);
    // Same glow floor as unmerged materials (AssetLibrary.tuneMaterial): emissive strength ≥ 2.5.
    const e = mat.emissive.clone().multiplyScalar(mat.emissive.getHex() !== 0 ? Math.max(mat.emissiveIntensity, 2.5) : 0);
    // Bake the material's base colour into the vertex colours.
    const col = g.getAttribute('color') as THREE.BufferAttribute | undefined;
    if (col && (mat.color.r !== 1 || mat.color.g !== 1 || mat.color.b !== 1)) {
      const k = [mat.color.r, mat.color.g, mat.color.b];
      for (let i = 0; i < col.count; i++) {
        for (let c = 0; c < Math.min(3, col.itemSize); c++) col.setComponent(i, c, col.getComponent(i, c) * (k[c] as number));
      }
    }
    for (let i = 0; i < n; i++) {
      rm[i * 2] = mat.roughness;
      rm[i * 2 + 1] = mat.metalness;
      em[i * 3] = e.r;
      em[i * 3 + 1] = e.g;
      em[i * 3 + 2] = e.b;
    }
    g.setAttribute('afRM', new THREE.BufferAttribute(rm, 2));
    g.setAttribute('afEm', new THREE.BufferAttribute(em, 3));
    return g;
  });
  const merged = mergeGeometries(geos, false);
  for (const g of geos) g.dispose();
  if (!merged) return null;
  const material = mergedMaterial();
  let mesh: THREE.Mesh;
  if (skinned) {
    const src = first as THREE.SkinnedMesh;
    const sm = new THREE.SkinnedMesh(merged, material);
    sm.bind(src.skeleton, src.bindMatrix);
    sm.bindMode = src.bindMode;
    mesh = sm;
  } else {
    mesh = new THREE.Mesh(merged, material);
  }
  mesh.position.copy(first.position);
  mesh.quaternion.copy(first.quaternion);
  mesh.scale.copy(first.scale);
  mesh.castShadow = first.castShadow;
  mesh.receiveShadow = first.receiveShadow;
  mesh.name = `${group.name}_merged`;
  // (Source geometries may be shared by another node that reuses the glTF mesh: leave them to GC.)
  for (const p of prims) group.remove(p);
  group.add(mesh);
  return mesh;
}

/**
 * Merges multi-primitive meshes throughout a freshly loaded glTF scene (before
 * any instance is cloned). Mesh groups keep their names (e.g. the breakable
 * `boss_behemoth_shell`), so hiding a group still hides its merged mesh.
 * Returns the number of draw calls saved.
 */
export function mergeModelPrimitives(root: THREE.Object3D): number {
  const groups: THREE.Object3D[] = [];
  root.traverse((o) => {
    let meshes = 0;
    for (const c of o.children) if ((c as THREE.Mesh).isMesh) meshes++;
    if (meshes >= 2) groups.push(o);
  });
  let saved = 0;
  for (const g of groups) {
    const before = g.children.filter((c) => (c as THREE.Mesh).isMesh).length;
    if (mergeGroup(g)) saved += before - 1;
  }
  return saved;
}
